/**
 * Every permission a role can grant: the single source of truth for the server and the clients. The clients'
 * shared package re-exports this file (packages/shared/src/data/permissions.ts), so it must stay free of
 * imports and server-only code.
 */
export const DefinedUserPermissions = {
	customer: {
		view: 'customer:view',
		add: 'customer:add',
		edit: 'customer:edit',
		delete: 'customer:delete',
		import: 'customer:import',
		export: 'customer:export',
	},
	stock: {
		view: 'stock:view',
		adjust: 'stock:adjust',
	},
	sale: {
		view: 'sale:view',
		viewAll: 'sale:view-all',
		add: 'sale:add',
		void: 'sale:void',
		refund: 'sale:refund',
		discount: 'sale:discount',
		priceOverride: 'sale:price-override',
		reprint: 'sale:reprint',
		approveRemoval: 'sale:approve-removal',
	},
	invoice: {
		view: 'invoice:view',
		add: 'invoice:add',
		edit: 'invoice:edit',
		delete: 'invoice:delete',
		void: 'invoice:void',
		send: 'invoice:send',
		export: 'invoice:export',
	},
	product: {
		view: 'product:view',
		add: 'product:add',
		edit: 'product:edit',
		delete: 'product:delete',
		editPrice: 'product:edit:price',
		import: 'product:import',
		export: 'product:export',
	},
	user: {
		view: 'user:view',
		add: 'user:add',
		edit: 'user:edit',
		delete: 'user:delete',
		assignRole: 'user:assign-role',
	},
	survey: {
		view: 'survey:view',
		add: 'survey:add',
		delete: 'survey:delete',
	},
	supplier: {
		view: 'supplier:view',
		add: 'supplier:add',
		edit: 'supplier:edit',
		delete: 'supplier:delete',
		import: 'supplier:import',
		pay: 'supplier:pay',
	},
	purchaseOrder: {
		view: 'purchase-order:view',
		add: 'purchase-order:add',
		edit: 'purchase-order:edit',
		delete: 'purchase-order:delete',
		receive: 'purchase-order:receive',
		approve: 'purchase-order:approve',
	},
	role: {
		add: 'role:add',
		view: 'role:view',
		edit: 'role:edit',
		delete: 'role:delete',
	},
	report: {
		view: 'report:view',
		export: 'report:export',
	},
	valueList: {
		view: 'value-list:view',
		add: 'value-list:add',
		edit: 'value-list:edit',
		delete: 'value-list:delete',
	},
	bill: {
		view: 'bill:view',
		add: 'bill:add',
		edit: 'bill:edit',
		delete: 'bill:delete',
	},
	productCategory: {
		view: 'product-category:view',
		add: 'product-category:add',
		edit: 'product-category:edit',
		delete: 'product-category:delete',
	},
	payment: {
		view: 'payment:view',
		add: 'payment:add',
	},
	pos: {
		open: 'pos:open',
		close: 'pos:close',
		cashMovement: 'pos:cash-movement',
	},
	posTerminal: {
		view: 'pos-terminal:view',
		add: 'pos-terminal:add',
		approve: 'pos-terminal:approve',
		block: 'pos-terminal:block',
		revoke: 'pos-terminal:revoke',
	},
} as const;

/** One permission, such as `'user:view'`. */
export type UserPermission = {
	[
		Group in keyof typeof DefinedUserPermissions
	]: (typeof DefinedUserPermissions)[Group][keyof (typeof DefinedUserPermissions)[Group]];
}[keyof typeof DefinedUserPermissions];

/** Every permission, sorted alphabetically. What an owner holds. */
export function DefinedUserPermissionsArray(): UserPermission[] {
	return Object.values(DefinedUserPermissions)
		.flatMap((group) => Object.values(group) as UserPermission[])
		.sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
}
